/* The same split as the ring, drawn as columns.
 *
 * A ring is good at one thing: showing that a few slices are most of the book.
 * It is bad at the next question, which is how the rest compare with each
 * other — two arcs of three and four percent are the same wedge to the eye, and
 * the only way to rank them is to read the labels, at which point the picture
 * is doing nothing. Columns on a shared baseline answer that at a glance, which
 * is the whole reason for having both.
 *
 * Hand-written SVG, like every other chart here (§9.4, §2). No library.
 *
 * ── what it borrows and what it does differently ─────────────────────────
 *
 * Same `Slice` list, same colours, same selection contract — tapping a column
 * selects it exactly as tapping an arc does, so the caption under the chart is
 * written once and serves both.
 *
 * Where it differs is labels. The ring leaves a slice under three percent
 * unlabelled because the leaders would stack into a ladder; a column always has
 * a place to put its name, directly underneath, so every column is named. The
 * names are turned on their side: a dozen tickers across a phone is about
 * twenty pixels each, which is narrower than "ZETA" laid flat.
 *
 * The percentage rides in the same turned label rather than sitting above the
 * column. Above was the first attempt and it was dead code in practice: a
 * dozen positions on a phone leaves each column about seventeen units wide,
 * under any threshold that keeps "10.4" from touching its neighbour, so the
 * figures never appeared at all. Turned on its side there is as much room as
 * the name needs, and it reads the way the ring's labels already do.
 *
 * ── the scale ────────────────────────────────────────────────────────────
 *
 * Full height is the largest slice, not a hundred percent. A book whose biggest
 * position is eleven percent would otherwise be a row of stubs along the floor,
 * which is a picture of nothing. The tallest column being the largest holding
 * is the fact worth reading; the axis is the other columns beside it. */

import { useId } from 'react';
import { money, pct } from '../lib/format';
import type { Slice } from './Donut';

const W = 320;
const H = 150;          // the plotting area, without the labels below
const LABEL_H = 64;     // room for "ZETA 35.1%" turned on its side
const TOP = 6;
const GAP_RATIO = 0.26; // of the column pitch
/* A slice worth nothing draws nothing, and a floor of one pixel would make an
   empty position look like a small one. */
const MIN_BAR = 1.5;

export function WeightBars({
  slices, selected, onSelect, currency, toDisplay,
}: {
  slices: Slice[];
  selected: string | null;
  onSelect: (key: string | null) => void;
  currency: 'USD' | 'PLN';
  toDisplay: (usd: number) => number;
}) {
  const titleId = useId();
  const visible = slices.filter((s) => s.weight > 0);
  if (visible.length === 0) return null;

  const ordered = [...visible].sort((a, b) => b.weight - a.weight);
  const tallest = Math.max(...ordered.map((s) => s.weight));
  const pitch = W / ordered.length;
  const barW = Math.max(2, pitch * (1 - GAP_RATIO));

  const spoken = ordered.slice(0, 6)
    .map((s) => `${s.label} ${s.weight.toFixed(1)} percent`).join(', ');

  return (
    <div className="bars-wrap">
      <svg
        className="weight-bars"
        viewBox={`0 0 ${W} ${TOP + H + LABEL_H}`}
        role="img"
        aria-labelledby={titleId}
        preserveAspectRatio="xMidYMid meet"
      >
        <title id={titleId}>
          {`Holdings by weight, tallest first. ${ordered.length} positions. ${spoken}.`}
        </title>

        {/* The floor. One line, because the scale is relative and a grid of
            percentages would be answering a question nobody asked of a
            proportion chart. */}
        <line
          x1={0} y1={TOP + H} x2={W} y2={TOP + H}
          stroke="var(--edge)" strokeWidth={1}
        />

        {ordered.map((s, i) => {
          const h = Math.max(MIN_BAR, (s.weight / tallest) * H);
          const x = i * pitch + (pitch - barW) / 2;
          const y = TOP + H - h;
          const dim = selected !== null && selected !== s.key;
          const label = s.short ?? s.label;
          return (
            <g key={s.key} onClick={() => onSelect(selected === s.key ? null : s.key)}
               style={{ cursor: 'pointer' }}>
              {/* A full-height target, so a one-percent column is as easy to
                  hit as the biggest one. */}
              <rect x={i * pitch} y={TOP} width={pitch} height={H + LABEL_H} fill="transparent" />
              <rect
                className="weight-bar"
                x={x} y={y} width={barW} height={h}
                rx={Math.min(3, barW / 2)}
                fill={s.color}
                data-dim={dim}
                data-on={selected === s.key}
              >
                <title>{`${s.label}: ${money(toDisplay(s.value), currency)}, ${pct(s.weight)}`}</title>
              </rect>

              {/* Turned on its side: a dozen tickers across a phone is about
                  seventeen units each, and "ZETA" laid flat does not fit. The
                  weight rides along, because vertical room is the one thing
                  this layout has spare. */}
              <text
                className="bar-label"
                x={x + barW / 2}
                y={TOP + H + 6}
                textAnchor="end"
                transform={`rotate(-90 ${x + barW / 2} ${TOP + H + 6})`}
                data-dim={dim}
              >
                {label} <tspan className="bar-pct num">{pct(s.weight)}</tspan>
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
